/* Green Gorilla Fruits — header
 * --------------------------------------------------------------
 * Sticky header scroll effect + search/mobile drawer toggles.
 */
(function () {
  'use strict';

  const SCROLL_THRESHOLD = 80;

  function bindStickyScroll(header) {
    const update = () => {
      if (window.scrollY > SCROLL_THRESHOLD) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function setOpen(toggle, drawer, open, openClass) {
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    drawer.classList.toggle(openClass, open);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  // Keep <html> in sync with whether any header drawer is open, so CSS can
  // freeze body scroll. Cart drawer manages its own `gg-cart-open` class.
  function syncBodyLock(header) {
    const anyOpen = header.querySelector(
      '[data-gg-mobile-menu][aria-hidden="false"],' +
        '[data-gg-search-drawer][aria-hidden="false"],' +
        '[data-gg-account-dropdown][aria-hidden="false"]'
    );
    document.documentElement.classList.toggle('gg-drawer-open', !!anyOpen);
  }

  function closeAll(header, except) {
    header.querySelectorAll('[data-gg-search-drawer]').forEach((d) => {
      if (d === except) return;
      const t = header.querySelector('[data-gg-search-toggle]');
      setOpen(t, d, false, 'is-open');
    });
    header.querySelectorAll('[data-gg-mobile-menu]').forEach((d) => {
      if (d === except) return;
      const t = header.querySelector('[data-gg-mobile-menu-toggle]');
      setOpen(t, d, false, 'is-open');
    });
    header.querySelectorAll('[data-gg-account-dropdown]').forEach((d) => {
      if (d === except) return;
      const toggles = header.querySelectorAll('[data-gg-account-toggle]');
      toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      d.setAttribute('aria-hidden', 'true');
      d.classList.remove('is-open', 'is-mobile-open');
    });
  }

  function bindSearchToggle(header) {
    const toggle = header.querySelector('[data-gg-search-toggle]');
    const drawer = header.querySelector('[data-gg-search-drawer]');
    if (!toggle || !drawer) return;
    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      const open = drawer.getAttribute('aria-hidden') === 'false';
      if (!open) closeAll(header, drawer);
      setOpen(toggle, drawer, !open, 'is-open');
      syncBodyLock(header);
      if (!open) {
        const input = drawer.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    });
  }

  function bindMobileMenu(header) {
    const toggle = header.querySelector('[data-gg-mobile-menu-toggle]');
    const menu = header.querySelector('[data-gg-mobile-menu]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const open = menu.getAttribute('aria-hidden') === 'false';
      if (!open) closeAll(header, menu);
      setOpen(toggle, menu, !open, 'is-open');
      syncBodyLock(header);
    });
  }

  function bindAccountDropdown(header) {
    const toggles = header.querySelectorAll('[data-gg-account-toggle]');
    const dropdown = header.querySelector('[data-gg-account-dropdown]');
    if (!toggles.length || !dropdown) return;
    const panel = dropdown.querySelector('[data-gg-account-panel]');
    const mq = window.matchMedia('(max-width: 767px)');
    const items = () => Array.from(dropdown.querySelectorAll('[role="menuitem"]'));
    let lastToggle = null;

    const open = () => {
      const mobile = mq.matches;
      dropdown.setAttribute('aria-hidden', 'false');
      dropdown.classList.toggle('is-open', !mobile);
      dropdown.classList.toggle('is-mobile-open', mobile);
      // Only the mobile slide-in behaves like a modal; the desktop popover is a non-modal flyover.
      if (panel) {
        if (mobile) panel.setAttribute('aria-modal', 'true');
        else panel.removeAttribute('aria-modal');
      }
      toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      syncBodyLock(header);
      const first = items()[0];
      if (first) first.focus();
    };

    const close = (restoreFocus) => {
      dropdown.setAttribute('aria-hidden', 'true');
      dropdown.classList.remove('is-open', 'is-mobile-open');
      if (panel) panel.removeAttribute('aria-modal');
      toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      syncBodyLock(header);
      if (restoreFocus && lastToggle) lastToggle.focus();
    };

    const isOpen = () => dropdown.getAttribute('aria-hidden') === 'false';

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isOpen()) {
          close(false);
        } else {
          lastToggle = toggle;
          closeAll(header, dropdown);
          open();
        }
      });
    });

    const closeBtn = dropdown.querySelector('[data-gg-account-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => close(true));
    }

    const backdrop = dropdown.querySelector('[data-gg-account-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', () => close(false));
    }

    dropdown.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const list = items();
        if (!list.length) return;
        e.preventDefault();
        const current = list.indexOf(document.activeElement);
        const next =
          e.key === 'ArrowDown'
            ? (current + 1) % list.length
            : (current - 1 + list.length) % list.length;
        list[next].focus();
      } else if (e.key === 'Home') {
        const list = items();
        if (list.length) {
          e.preventDefault();
          list[0].focus();
        }
      } else if (e.key === 'End') {
        const list = items();
        if (list.length) {
          e.preventDefault();
          list[list.length - 1].focus();
        }
      }
    });

    document.addEventListener('mousedown', (e) => {
      if (!isOpen()) return;
      if (dropdown.contains(e.target)) return;
      let onToggle = false;
      toggles.forEach((t) => {
        if (t.contains(e.target)) onToggle = true;
      });
      if (onToggle) return;
      close(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen()) close(true);
    });

    mq.addEventListener('change', () => {
      if (isOpen()) close(false);
    });
  }

  function bindEscapeClose(header) {
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      closeAll(header);
      syncBodyLock(header);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-gg-header]').forEach((header) => {
      bindStickyScroll(header);
      bindSearchToggle(header);
      bindMobileMenu(header);
      bindAccountDropdown(header);
      bindEscapeClose(header);
    });
  });
})();
